import React, { useCallback, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';

import head from 'lodash/head';

import {
  ChangeMaterialsFiltersFunc,
  FetchMaterialsCacheKey,
  FetchMaterialsFilterFields,
  FetchMaterialsFilterMaterials,
  FetchMaterialsFilters,
  FetchMaterialsLimit,
  FetchMaterialsPage,
  FetchMaterialsSort,
  FetchMaterialsSortTypes,
  MaterialCategoryID,
  MaterialNanoID,
  UpdateMaterialCacheAction
} from '../../../../materials/materialsTypes';
import { TeamNanoID, TeamUUID } from '../../../../teams/teamsTypes';
import {
  ErrorMessage,
  FetchItemsLoadMoreItems,
  FetchItemsSort,
  IsFetched
} from '../../../../../types';
import {
  FetchMaterialsSetsCacheKeys,
  UpdateMaterialsSetCacheAction
} from '../../../../materialsSets/materialsSetsTypes';

import { useThreeDStockContentView } from '../../../hooks/useThreeDStockContentView';
import { useThreeDStockContentScale } from '../../../hooks/useThreeDStockContentScale';
import { useMaterialsSortDropdown } from '../../../../materials/hooks/useMaterialsSortDropdown';
import { useCurrentUser } from '../../../../../auth/hooks/useAuth';
import { useThreeDStockMaterialsContentCheckedCategories } from './hooks/useThreeDStockMaterialsContentCheckedCategories';

import { ThreeDStockContentHeader } from '../../headers/ThreeDStockContentHeader';
import { ThreeDStockContentSortAndView } from '../../headers/ThreeDStockContentSortAndView';
import { ThreeDStockContentSortDropdownButtonButtonsType } from '../../headers/ThreeDStockContentSortAndView/components/ThreeDStockContentSortDropdownButton';
import {
  ThreeDStockMaterialsList,
  ThreeDStockMaterialsListMaterial,
  ThreeDStockMaterialsListMaterials,
  ThreeDStockMaterialsListMaterialsSet
} from '../../lists/ThreeDStockMaterialsList';
import { ThreeDStockMaterialsCompactList } from '../../lists/ThreeDStockMaterialsCompactList';
import { ThreeDStockMaterialsFilters } from '../../filters/ThreeDStockMaterialsFilters';

import { isEveryPropEmpty } from '../../../../../utils/isEveryPropEmpty';

import { StockPermissions, StockTabs } from '../../../stockConstants';
import { MaterialsPermissions } from '../../../../materials/materialsConstants';

import { words } from '../../../../../locales/keys';

const materialsSortI18nTextTypes = {
  [FetchMaterialsSortTypes.CREATED_AT_DESC]: words.newest,
  [FetchMaterialsSortTypes.SELECTED_MATERIALS_COUNT_DESC]: words.mostUsed,
  [FetchMaterialsSortTypes.USERS_CHOSEN_MATERIALS_COUNT_DESC]: words.mostLiked
};

const allFilterFields = [
  FetchMaterialsFilterFields.BRAND_ID,
  FetchMaterialsFilterFields.BLOCKED,
  FetchMaterialsFilterFields.FAVORITE,
  FetchMaterialsFilterFields.CURRENT_TEAM_FAVORITE,
  FetchMaterialsFilterFields.TEAM_ID_FAVORITE
];

const tab = StockTabs.MATERIALS;

interface ThreeDStockMaterialsContentProps {
  materialsCacheKey: FetchMaterialsCacheKey;
  materialsFilters: FetchMaterialsFilters;
  changeMaterialsFilters: ChangeMaterialsFiltersFunc;
  filterMaterials: FetchMaterialsFilterMaterials;
  materialsSort: FetchItemsSort;
  sortMaterials: (nextSort: FetchMaterialsSort) => void;
  materials: ThreeDStockMaterialsListMaterials;
  updateMaterialCache?: UpdateMaterialCacheAction<ThreeDStockMaterialsListMaterial>;
  onMaterialEditButtonMouseEnter?: (materialNanoId: MaterialNanoID) => void;
  materialsError: ErrorMessage;
  materialsFetched: IsFetched;
  materialsIsPlaceholderData: boolean;
  materialsTotalCount: number;
  hasNextMaterialsPage: boolean;
  materialsPage: FetchMaterialsPage;
  materialsLimit: FetchMaterialsLimit;
  loadMoreMaterials: FetchItemsLoadMoreItems;
  materialsFetchingNextPage: boolean;
  materialsSet: ThreeDStockMaterialsListMaterialsSet | null;
  materialsSetCacheKeys?: FetchMaterialsSetsCacheKeys;
  updateMaterialsSetCache: UpdateMaterialsSetCacheAction<ThreeDStockMaterialsListMaterialsSet>;
  changeMaterialsCategoryFilters: (
    tab?: StockTabs,
    changeFilters?: FetchMaterialsFilters,
    removeFilters?: string[]
  ) => void;
  defaultMaterialCategoryIds?: MaterialCategoryID[];
  defaultMaterialParentCategoryIds?: MaterialCategoryID[];
  clearMaterialsFilters?: (
    persistFiltersFields?: FetchMaterialsFilterFields[]
  ) => void;
  isMyLibrary?: boolean;
  companyNanoId?: TeamNanoID;
  teamUuid?: TeamUUID;
  isModal?: boolean;
}

function ThreeDStockMaterialsContent({
  materialsCacheKey,
  materialsFilters,
  changeMaterialsFilters,
  filterMaterials,
  materialsSort,
  sortMaterials,
  materials,
  updateMaterialCache,
  onMaterialEditButtonMouseEnter,
  materialsError,
  materialsFetched,
  materialsIsPlaceholderData,
  materialsTotalCount,
  hasNextMaterialsPage,
  materialsPage,
  materialsLimit,
  loadMoreMaterials,
  materialsFetchingNextPage,
  materialsSet,
  materialsSetCacheKeys,
  updateMaterialsSetCache,
  changeMaterialsCategoryFilters,
  clearMaterialsFilters,
  defaultMaterialCategoryIds,
  defaultMaterialParentCategoryIds,
  companyNanoId,
  teamUuid,
  isMyLibrary,
  isModal
}: ThreeDStockMaterialsContentProps) {
  const currentUser = useCurrentUser();

  const [showFilters, setShowFilters] = useState(true);

  const { checkedCategories, setCheckedCategories } =
    useThreeDStockMaterialsContentCheckedCategories();

  const closeFilters = useCallback(() => {
    setShowFilters(false);
  }, []);

  const openFilters = useCallback(() => {
    setShowFilters(true);
  }, []);

  const withFilters = useMemo<boolean>(
    () =>
      !isEveryPropEmpty(pick(materialsFilters, allFilterFields)) ||
      !isEmpty(checkedCategories),
    [materialsFilters, checkedCategories]
  );

  const { isGridView } = useThreeDStockContentView({ tab });
  const { scale } = useThreeDStockContentScale({ tab });

  const { handleSortByMostLiked, handleSortByMostUsed, handleSortByNewest } =
    useMaterialsSortDropdown({
      materialsBaseCacheKey: materialsCacheKey,
      sortMaterials
    });

  const handleChangeCategory = useCallback<
    (changeFilters?: FetchMaterialsFilters, removeFilters?: string[]) => void
  >(
    (changeFilters, removeFilters) => {
      changeMaterialsCategoryFilters(
        StockTabs.MATERIALS,
        changeFilters,
        removeFilters
      );
    },
    [changeMaterialsCategoryFilters]
  );

  const sortBy = useMemo(() => head(materialsSort), [materialsSort]);

  const sortButtons = useMemo<
    ThreeDStockContentSortDropdownButtonButtonsType[]
  >(
    () => [
      {
        id: 'newest',
        i18nText: words.newest,
        onSelect: handleSortByNewest,
        active: sortBy === FetchMaterialsSortTypes.CREATED_AT_DESC
      },
      {
        id: 'mostUsed',
        i18nText: words.mostUsed,
        onSelect: handleSortByMostUsed,
        active: sortBy === FetchMaterialsSortTypes.SELECTED_MATERIALS_COUNT_DESC
      },
      {
        id: 'mostLiked',
        i18nText: words.mostLiked,
        onSelect: handleSortByMostLiked,
        active:
          sortBy === FetchMaterialsSortTypes.USERS_CHOSEN_MATERIALS_COUNT_DESC
      }
    ],
    [handleSortByMostLiked, handleSortByMostUsed, handleSortByNewest, sortBy]
  );

  return (
    <div
      id="3d-stock-container"
      className="items-start p-4 md:p-8 flex flex-col gap-8 2xl:gap-x-11"
    >
      <ThreeDStockContentHeader
        tab={tab}
        categoryIds={defaultMaterialCategoryIds}
        changeItemsCategoryFilters={changeMaterialsCategoryFilters}
        isLibrary={isMyLibrary || !!companyNanoId}
      />
      <div
        id="3d-stock-wrapper"
        className="flex w-full gap-8 2xl:gap-x-11 items-start"
      >
        {showFilters && (
          <ThreeDStockMaterialsFilters
            teamUuid={teamUuid}
            changeMaterialsFilters={changeMaterialsFilters}
            filterMaterials={filterMaterials}
            materialsFilters={materialsFilters}
            closeFilters={closeFilters}
            clearFilters={clearMaterialsFilters}
            defaultMaterialCategoryIds={defaultMaterialCategoryIds}
            defaultMaterialParentCategoryIds={defaultMaterialParentCategoryIds}
            checkedCategories={checkedCategories}
            setCheckedCategories={setCheckedCategories}
            changeCategory={handleChangeCategory}
            clientIds={materialsFilters?.clientIds}
            isModal={isModal}
          />
        )}

        <div className="flex flex-col w-full gap-8 2xl:gap-x-11 min-w-0">
          <ThreeDStockContentSortAndView
            tab={tab}
            currentSortI18nText={materialsSortI18nTextTypes[sortBy]}
            sortButtons={sortButtons}
            totalCount={materialsTotalCount}
            page={materialsPage}
            limit={materialsLimit}
            searchText={
              materialsFilters.nameSkuTags || materialsFilters.name?.ilike
            }
            withOpenFiltersButton={!showFilters}
            withSelectedFilters={withFilters}
            openFilters={openFilters}
            withTotal={currentUser.hasPermissions(
              MaterialsPermissions.READ_MATERIALS_TOTAL_COUNT
            )}
            withImageScaleButton={currentUser.hasPermissions(
              MaterialsPermissions.READ_MATERIALS_IMAGE_SCALE
            )}
            withImageToggleViewButton={currentUser.hasPermissions(
              MaterialsPermissions.READ_MATERIALS_TOGGLE_VIEW_BUTTON
            )}
            withEditMaterialsButton={
              !isModal &&
              !companyNanoId &&
              !isMyLibrary &&
              currentUser.hasPermissions(
                StockPermissions.READ_MATERIALS_EDIT_BUTTON
              )
            }
            withCompanyLibraryEditMaterialsButton={
              !isModal &&
              (isMyLibrary || !!companyNanoId) &&
              currentUser.hasPermissions(
                isMyLibrary
                  ? MaterialsPermissions.READ_SELF_COMPANY_LIBRARY_EDIT_MATERIALS_BUTTON
                  : MaterialsPermissions.READ_OTHER_COMPANY_LIBRARY_EDIT_MATERIALS_BUTTON
              )
            }
            companyNanoId={companyNanoId}
            isMyLibrary={isMyLibrary}
          />

          {isGridView ? (
            <ThreeDStockMaterialsList
              cellMinSize={scale}
              onMaterialEditButtonMouseEnter={onMaterialEditButtonMouseEnter}
              materials={materials}
              materialsError={materialsError}
              materialsFetched={materialsFetched}
              materialsIsPlaceholderData={materialsIsPlaceholderData}
              materialsTotalCount={materialsTotalCount}
              changeMaterialsFilters={changeMaterialsFilters}
              materialsFilters={materialsFilters}
              hasNextMaterialsPage={hasNextMaterialsPage}
              materialsPage={materialsPage}
              materialsLimit={materialsLimit}
              loadMoreMaterials={loadMoreMaterials}
              materialsFetchingNextPage={materialsFetchingNextPage}
              materialsSet={materialsSet}
              updateMaterialsSetCache={updateMaterialsSetCache}
              materialsSetCacheKeys={materialsSetCacheKeys}
              updateMaterialCache={updateMaterialCache}
              companyNanoId={companyNanoId}
              isMyLibrary={isMyLibrary}
            />
          ) : (
            <ThreeDStockMaterialsCompactList
              onMaterialEditButtonMouseEnter={onMaterialEditButtonMouseEnter}
              materials={materials}
              materialsError={materialsError}
              materialsFetched={materialsFetched}
              materialsIsPlaceholderData={materialsIsPlaceholderData}
              materialsTotalCount={materialsTotalCount}
              changeMaterialsFilters={changeMaterialsFilters}
              materialsFilters={materialsFilters}
              hasNextMaterialsPage={hasNextMaterialsPage}
              materialsPage={materialsPage}
              materialsLimit={materialsLimit}
              loadMoreMaterials={loadMoreMaterials}
              materialsFetchingNextPage={materialsFetchingNextPage}
              updateMaterialCache={updateMaterialCache}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default ThreeDStockMaterialsContent;
